import React, { type FC, useCallback, useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { deleteQuickApp, getQuickApps, getQuickAppsMetaInfo } from 'client/api';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { routes } from 'client/lib/routes';
import { IQuickApp, QuickAppError, QuickAppStatus, TQuickAppsMeta } from 'client/model/privateApps';
import { Channel } from 'client/model/skill';
import * as keyset from 'i18n/PrivateApps';
import * as resourcesKeyset from 'i18n/Resources';

import { QuickAppsPage } from './QuickAppsPage/QuickAppsPage';
import { QuickAppsStartPage } from './QuickAppsStartPage/QuickAppsStartPage';

const APPS_UPDATE_INTERVAL_MS = 60 * 1000;
const QUICK_APP_TOAST = 'QUICK_APP_TOAST';

const t = i18n(keyset);
const tResources = i18n(resourcesKeyset);

export const QuickApps: FC = () => {
    const [quickApps, setQuickApps] = useState<Partial<IQuickApp>[]>();
    const [meta, setMeta] = useState<TQuickAppsMeta>();
    const [shouldUpdateApps, setShouldUpdateApps] = useState<Date>();
    const [errorIdsSet, setErrorIdsSet] = useState<Set<string>>(new Set());

    const { add: addToast } = useToaster();

    const fetchApps = useCallback(async () => {
        try {
            const quickAppResponse = await getQuickApps();
            if (Array.isArray(quickAppResponse)) {
                setQuickApps(quickAppResponse);
                setShouldUpdateApps(new Date());

                for (const app of quickAppResponse) {
                    if (app.status === QuickAppStatus.ERROR && app.error && !errorIdsSet.has(app.id)) {
                        setErrorIdsSet((state) => new Set(state.add(app.id)));
                        let errorMessage = t('error');
                        if (app.error === QuickAppError.APK_DECLINED) {
                            errorMessage = t('apk-declined');
                        }
                        addToast({
                            title: app.name,
                            content: errorMessage,
                            theme: 'danger',
                            name: `${QUICK_APP_TOAST}_${app.id}`,
                            autoHiding: false,
                            isClosable: true,
                        });
                    }
                }
            }
        } catch (_error) {
            addToast({
                title: t('error'),
                theme: 'danger',
                name: QUICK_APP_TOAST,
            });
        }
    }, [addToast, errorIdsSet]);

    const fetchMeta = useCallback(async () => {
        try {
            const metaResponse = await getQuickAppsMetaInfo();
            if ('storage_info' in metaResponse) {
                setMeta(metaResponse.storage_info);
            }
        } catch (error) {
            console.error('@fetchMeta', error);
        }
    }, []);

    useEffect(() => {
        fetchApps();
        fetchMeta();
    }, [fetchApps, fetchMeta]);

    useEffect(() => {
        const interval = setInterval(fetchApps, APPS_UPDATE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [fetchApps, shouldUpdateApps]);

    const onAdd = useCallback(
        (channel: Channel) => {
            setQuickApps((state) => (state ? [...state, { channel, name: 'dummy_loading_state_app' }] : state));
            fetchApps();
            fetchMeta();
        },
        [fetchApps, fetchMeta],
    );

    const onDelete = useCallback(
        async (deletedId: string, channel: Channel) => {
            try {
                await deleteQuickApp(deletedId, channel);
                await fetchMeta();
                setQuickApps((state) => state?.filter((app) => app.id !== deletedId));
                setShouldUpdateApps(new Date());
            } catch (_error) {
                addToast({
                    title: tResources('delete-error'),
                    theme: 'danger',
                    name: 'CARDS_FETCH_ERROR',
                    autoHiding: false,
                    isClosable: true,
                });
            }
        },
        [addToast, fetchMeta],
    );

    return (
        <ErrorBoundary withHomepageLink>
            <Switch>
                <Route path={routes.app.quickApps('start')}>
                    <QuickAppsStartPage />
                </Route>
                <Route path={routes.app.quickApps()} exact>
                    <QuickAppsPage quickApps={quickApps} meta={meta} onAdd={onAdd} onDelete={onDelete} />
                </Route>
                <Redirect to={routes.app.root} />
            </Switch>
        </ErrorBoundary>
    );
};
